import React, { PureComponent } from 'react';

import _get from 'lodash.get';
import _set from 'lodash.set';

import { MapperAPI } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { array, arrayOf, componentName, element, mapper, object, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { enumValues, getLostProps, propagateData } from '@philips-emr/tws-utils';

function fromEntries(result, [key, value]) {
  result[key] = value;
  return result;
}

function normalizeEntry([sourceKey, sourceValue]) {
  return ([targetKey, targetValue]) => {
    return [
      targetKey.replace(`${sourceKey}.`, ''),
      targetValue.replace(`${sourceValue}.`, ''),
    ];
  };
}

function entriesFrom(targetKey) {
  return ([key]) => key.startsWith(`${targetKey}.`);
}

function isObject(target) {
  return typeof target === 'object';
}

const acceptedTypes = enumValues(ComponentType);

@AwesomeComponent({ type: ComponentType.BEHAVIOR, api: MapperAPI })
class Mapper extends PureComponent {
  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    component: componentName(ComponentType.DATA_DISPLAY),
    customMapping: object.notEnumerable(),
    data: oneOfType([object, array]).notEnumerable(),
    map: mapper,
    uuid: string,
  };

  static defaultProps = {
    map: {},
    customMapping: {},
  };

  mapObject = (data, map) => {
    if (!isObject(data)) return data;

    const result = { ...data };

    const mapEntries = Object.entries(map);

    mapEntries.forEach((entry) => {
      const [componentKey, dataKey] = entry;
      let value = _get(data, dataKey);

      if (!value) return;

      if (Array.isArray(value)) {
        const valueMap = mapEntries
          .filter(entriesFrom(componentKey))
          .map(normalizeEntry(entry))
          .reduce(fromEntries, {});

        value = this.map(value, valueMap);
      }

      _set(result, componentKey, value);
    });

    return this.applyCustomMapping(data, result);
  };

  applyCustomMapping = (originalData, mappedData) => {
    const { customMapping } = this.props || {};

    const names = Object.keys(customMapping);
    names.forEach(name => {
      const fn = customMapping[name];
      if (typeof fn === 'function') {
        mappedData[name] = fn(originalData);
      }
    });
    return mappedData;
  };

  map = (data, map) => {
    if (Array.isArray(data)) {
      return data.map(item => this.mapObject(item, map));
    }
    return this.mapObject(data, map);
  };

  render() {
    const { children, uuid, data, map } = this.props;
    const lostProps = getLostProps(Mapper.propTypes, this.props);

    if (React.Children.count(children) === 0) {
      const Container = ContainerProvider.provide(this);
      return <Container acceptedTypes={acceptedTypes} className="u-expand" uuid={uuid} />;
    }

    const mappedData = this.map(data, map);

    return propagateData(children, mappedData, lostProps);
  }
}

export default Mapper;
